import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useApplicationFormApi } from './applicationFormApi';
import { SearchableLookupField } from '../../leads/wizard/SearchableLookupField';

interface Step4Props {
  studentId: number | null;
}

const emptyDraft = {
  name: '',
  institution: '',
  majorSubject: '',
  gpa: '',
  fromDate: '',
  toDate: '',
  academicLevelId: '' as number | '',
  countryId: '' as number | '',
  completionStatus: '' as string,
};

export const Step4HigherEducation = ({ studentId }: Step4Props) => {
  const { listLookup, listQualifications, createQualification, deleteQualification } = useApplicationFormApi();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(emptyDraft);

  const levelsQuery = useQuery({
    queryKey: ['lookup', '/master-data/academic-levels'],
    queryFn: () => listLookup('/master-data/academic-levels', { activeOnly: true }),
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });
  const qualificationsQuery = useQuery({
    queryKey: ['qualifications', studentId],
    queryFn: () => listQualifications(studentId!),
    enabled: !!studentId,
  });

  const addMutation = useMutation({
    mutationFn: () =>
      createQualification(studentId!, {
        name: draft.name.trim(),
        institution: draft.institution.trim(),
        majorSubject: draft.majorSubject.trim() || undefined,
        gpa: draft.gpa ? Number(draft.gpa) : undefined,
        fromDate: draft.fromDate || undefined,
        toDate: draft.toDate || undefined,
        academicLevelId: draft.academicLevelId || undefined,
        countryId: draft.countryId || undefined,
        completionStatus: draft.completionStatus || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualifications', studentId] });
      setDraft(emptyDraft);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteQualification(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qualifications', studentId] }),
  });

  if (!studentId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Complete Step 2 first — qualifications are saved directly against the student.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <List dense>
        {qualificationsQuery.data?.map((q) => (
          <ListItem
            key={q.id}
            divider
            secondaryAction={
              <IconButton size="small" onClick={() => deleteMutation.mutate(q.id)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            }
          >
            <ListItemText
              primary={`${q.name} — ${q.institution}`}
              secondary={[q.academicLevel?.name, q.majorSubject, q.gpa ? `GPA ${q.gpa}` : null, q.completionStatus]
                .filter(Boolean)
                .join(' · ')}
            />
          </ListItem>
        ))}
        {qualificationsQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No qualifications added yet.
          </Typography>
        )}
      </List>

      <Typography variant="subtitle2">Add qualification</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Qualification / degree name"
          fullWidth
          size="small"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <TextField
          label="University / Institution"
          fullWidth
          size="small"
          value={draft.institution}
          onChange={(e) => setDraft((d) => ({ ...d, institution: e.target.value }))}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Qualification level"
          fullWidth
          size="small"
          value={draft.academicLevelId}
          onChange={(e) => setDraft((d) => ({ ...d, academicLevelId: e.target.value ? Number(e.target.value) : '' }))}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {levelsQuery.data?.map((l) => (
            <MenuItem key={l.id as number} value={l.id as number}>
              {String(l.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Major"
          fullWidth
          size="small"
          value={draft.majorSubject}
          onChange={(e) => setDraft((d) => ({ ...d, majorSubject: e.target.value }))}
        />
        <TextField
          label="GPA"
          type="number"
          fullWidth
          size="small"
          value={draft.gpa}
          onChange={(e) => setDraft((d) => ({ ...d, gpa: e.target.value }))}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SearchableLookupField
          label="Country"
          size="small"
          options={countriesQuery.data}
          value={draft.countryId}
          onChange={(id) => setDraft((d) => ({ ...d, countryId: id }))}
        />
        <TextField
          label="From"
          type="date"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          value={draft.fromDate}
          onChange={(e) => setDraft((d) => ({ ...d, fromDate: e.target.value }))}
        />
        <TextField
          label="To"
          type="date"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          value={draft.toDate}
          onChange={(e) => setDraft((d) => ({ ...d, toDate: e.target.value }))}
        />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select
          label="Completion status"
          fullWidth
          size="small"
          value={draft.completionStatus}
          onChange={(e) => setDraft((d) => ({ ...d, completionStatus: e.target.value }))}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
          <MenuItem value="ONGOING">Ongoing</MenuItem>
          <MenuItem value="INCOMPLETE">Incomplete</MenuItem>
        </TextField>
        <Button
          startIcon={<AddIcon />}
          disabled={!draft.name.trim() || !draft.institution.trim() || addMutation.isPending}
          onClick={() => addMutation.mutate()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add More
        </Button>
      </Stack>
    </Stack>
  );
};
